import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  /** Schema.org @type — defaults to WebApplication. Use 'Article' for editorial pages. */
  schemaType?: 'WebApplication' | 'Article' | 'BlogPosting' | 'WebPage' | 'Product';
  /** Optional override for the full JSON-LD payload (skips the auto-built one). */
  structuredData?: Record<string, any>;
  /** ISO date for Article schema */
  datePublished?: string;
  dateModified?: string;
  author?: string;
}

/**
 * SEO Head Component
 * Dynamically updates meta tags + JSON-LD for the current route.
 */
export const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'Vergo | The Ultimate AI Chatbot Hub & Search Engine',
  description = 'Discover, compare, and use the best AI tools in one place. Vergo is your all-in-one AI hub for productivity, research, and automation.',
  keywords = 'AI chatbot, AI assistant, chatbot comparison, GPT-4, Claude, Gemini, AI tools, chatbot directory, AI recommendations, best AI chatbots',
  ogImage = '/og-image.png',
  canonical,
  schemaType = 'WebApplication',
  structuredData,
  datePublished,
  dateModified,
  author = 'Vergo Editorial Team',
}) => {
  const location = useLocation();
  const baseUrl = 'https://vergoai.app';
  const currentUrl = canonical || `${baseUrl}${location.pathname}`;

  useEffect(() => {
    document.title = title;

    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', author);
    updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    updateMetaTag('googlebot', 'index, follow');

    updateMetaProperty('og:type', schemaType === 'Article' || schemaType === 'BlogPosting' ? 'article' : 'website');
    updateMetaProperty('og:site_name', 'Vergo');
    updateMetaProperty('og:title', title);
    updateMetaProperty('og:description', description);
    updateMetaProperty('og:url', currentUrl);
    updateMetaProperty('og:image', ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`);
    updateMetaProperty('og:image:width', '1200');
    updateMetaProperty('og:image:height', '630');
    updateMetaProperty('og:locale', 'en_US');

    updateMetaProperty('twitter:card', 'summary_large_image');
    updateMetaProperty('twitter:title', title);
    updateMetaProperty('twitter:description', description);
    updateMetaProperty('twitter:image', ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`);
    updateMetaProperty('twitter:creator', '@VergoAI');

    updateCanonical(currentUrl);

    const jsonLd =
      structuredData ??
      (schemaType === 'Article' || schemaType === 'BlogPosting'
        ? {
            '@context': 'https://schema.org',
            '@type': schemaType,
            headline: title,
            description,
            url: currentUrl,
            image: ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`,
            datePublished: datePublished || '2026-01-01',
            dateModified: dateModified || datePublished || new Date().toISOString().slice(0, 10),
            author: { '@type': 'Organization', name: author },
            publisher: {
              '@type': 'Organization',
              name: 'Vergo',
              logo: { '@type': 'ImageObject', url: `${baseUrl}/favicon.png` },
            },
            mainEntityOfPage: { '@type': 'WebPage', '@id': currentUrl },
          }
        : {
            '@context': 'https://schema.org',
            '@type': schemaType,
            name: 'Vergo',
            url: baseUrl,
            description,
            applicationCategory: 'BusinessApplication',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.8', reviewCount: '1250' },
          });

    updateStructuredData(jsonLd);
  }, [title, description, keywords, ogImage, currentUrl, baseUrl, schemaType, structuredData, datePublished, dateModified, author]);

  return null;
};

function updateMetaTag(name: string, content: string) {
  let element = document.querySelector(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function updateMetaProperty(property: string, content: string) {
  let element = document.querySelector(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function updateCanonical(url: string) {
  // Remove any stale canonical(s) so we never ship duplicates from index.html + dynamic.
  document.querySelectorAll('link[rel="canonical"]').forEach((el, i) => {
    if (i > 0) el.remove();
  });
  let element = document.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', url);
}

function updateStructuredData(data: Record<string, any>) {
  let script = document.querySelector('script[type="application/ld+json"]#dynamic-structured-data');
  if (!script) {
    script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('id', 'dynamic-structured-data');
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}
